import { AfterViewInit, Component, ElementRef, OnDestroy, computed, effect, input, model, output, signal, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ModalTypes } from '../../core/enums/base.enums';
import { InputLabel } from "../input-label/input-label.component";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormValidatorMessages } from '../../core/enums/forms.enums';
import { CustomValidators } from '../../core/validators/custom-validators';
import { EscCloseDirective } from '../../core/directives/esc-close.directive';
import { TooltipDirective } from '../../core/directives/tooltip.directive';

@Component({
  selector: 'ms-confirmation-modal',
  imports: [CommonModule, FontAwesomeModule, InputLabel, ReactiveFormsModule, EscCloseDirective, TooltipDirective],
  templateUrl: './confirmation-modal.component.html'
})

export class ConfirmationModalComponent implements AfterViewInit, OnDestroy {
  public modalId = input<string>();
  public modalType = model<ModalTypes>(ModalTypes.Exclamation);
  public ModalTypes = ModalTypes;

  public FormValidatorMessages = FormValidatorMessages;
  
  public title = model<string>();
  public message = model<string | undefined>();
  
  public enableNotes = model<boolean>(false);
  public isNoteRequired = model<boolean>(false);

  public enableTypeToConfirm = model<boolean>(false);
  public typeToConfirmText = model<string>("CONFIRM");

  public confirmButtonText = model<string>("Ok");
  public cancelButtonText = model<string>("Cancel");
  public confirmButtonClassName = model<string>("btn-primary");

  /** Optional hover and focus help on the two footer buttons. Empty leaves the button bare. */
  public confirmButtonTooltip = model<string>("");
  public cancelButtonTooltip = model<string>("");
  
  public confirmed = output<string | undefined>();
  public cancelled = output<void>();

  /** Two-way Bind to isSubmitting with a model to show
   * a loading spinner  */
  public isSubmitting = model<boolean | undefined>(undefined);

  /** True from Bootstrap's hide.bs.modal until hidden.bs.modal — the ~150ms fade-out window
   * during which the modal is still visible and clickable but the parent has already flipped
   * isSubmitting back to false. Without this, a spam-click at the tail of the fade would
   * re-enter onConfirm and fire the request a second time. */
  private isClosing = signal(false);

  /** Set synchronously inside onConfirm/onCancel right before emit, released on hidden.bs.modal.
   * Prevents a double-emit in two races the other locks don't catch:
   *   1. The parent clears isSubmitting back to false before CD propagates it to the child
   *      (e.g., keyboard Enter-Enter synthesised within one macrotask).
   *   2. BootstrapService.manageModal queues a Hide requested during fade-in (see
   *      bootstrap.service.ts — animatingModals / queuedModalActions) and only flushes it on
   *      shown.bs.modal, so hide.bs.modal has not fired yet and isClosing is still false.
   * The first click still fires — only subsequent clicks during the same open-emit-hide cycle
   * are suppressed. Released on hidden.bs.modal so the modal is usable on its next show. */
  private emittedAwaitingHide = signal(false);

  /** Buttons are inert whenever the parent is submitting, Bootstrap is fading the modal out,
   * or we've already emitted confirmed/cancelled for this open cycle and are waiting for the
   * hide to complete. Template bindings and method guards both read this to keep the two in
   * lock-step. */
  public readonly isLocked = computed(
    () => this.isSubmitting() === true || this.isClosing() || this.emittedAwaitingHide()
  );

  private modalElement = viewChild.required<ElementRef<HTMLElement>>('modalRoot');
  private handleHide = (): void => this.isClosing.set(true);
  private handleHidden = (): void => {
    this.isClosing.set(false);
    this.emittedAwaitingHide.set(false);
  };

  public confirmModalForm: FormGroup;

  constructor(private fb: FormBuilder) {
    this.confirmModalForm = this.fb.group({
      note: [undefined],
      typeToConfirm: [undefined]
    });

    effect(() => {
      const noteControl = this.confirmModalForm.get('note');
      if (this.isNoteRequired() === true) {
        noteControl?.setValidators([Validators.required]);
      } else {
        noteControl?.setValidators([]);
      }
      noteControl?.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      const typeToConfirmControl = this.confirmModalForm.get('typeToConfirm');
      if (this.enableTypeToConfirm() === true) {
        const confirmText = this.typeToConfirmText();
        typeToConfirmControl?.setValidators([
          Validators.required,
          CustomValidators.matchTypeToConfirm(confirmText)
        ]);
      } else {
        typeToConfirmControl?.setValidators([]);
      }
      typeToConfirmControl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  public onConfirm(): void {
    // Guards: spam-clicks while the parent is submitting, during fade-out, and during the window
    // between a successful emit and the resulting hide completing (covers queued-hide-during-fade-in
    // and the between-emit-and-CD race).
    if (this.isLocked()) return;

    if ((this.enableNotes() === true && this.confirmModalForm.invalid) ||
        (this.enableTypeToConfirm() === true && this.confirmModalForm.invalid)) {
      this.confirmModalForm.markAllAsTouched();
      return;
    }

    // Set the self-lock synchronously BEFORE emit so parent handlers that run during the emit
    // already see isLocked=true if they somehow re-enter. Released on hidden.bs.modal.
    this.emittedAwaitingHide.set(true);
    this.confirmed.emit(this.confirmModalForm.get('note')?.value);
    this.confirmModalForm.get("typeToConfirm")?.reset();
    this.confirmModalForm.get("note")?.reset();
  }

  public onCancel(): void {
    // Covers the Cancel button, the Esc key via msEscClose, and any other caller that reaches
    // onCancel while a submit is in flight or the modal is fading out.
    if (this.isLocked()) return;

    this.emittedAwaitingHide.set(true);
    this.confirmModalForm.get("typeToConfirm")?.reset();
    this.confirmModalForm.get("note")?.reset();
    this.cancelled.emit();
  }

  public ngAfterViewInit(): void {
    const el = this.modalElement().nativeElement;
    el.addEventListener('hide.bs.modal', this.handleHide);
    el.addEventListener('hidden.bs.modal', this.handleHidden);
  }

  public ngOnDestroy(): void {
    const el = this.modalElement().nativeElement;
    el.removeEventListener('hide.bs.modal', this.handleHide);
    el.removeEventListener('hidden.bs.modal', this.handleHidden);
  }
}
